import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import formatName from '../../utils/formatName';
import formatDateTime from '../../utils/formatDateTime';
import findColor from '../../utils/findColor';

import mapInteractions from '../../utils/mapInteractions';

const LocationTable = styled.table`
  width: 90%;
  max-width: 1000px;
  font-size: 0.75rem;
  margin: 0 auto 2rem;
  tr {
    line-height: 1.5rem;
    border-bottom: 1px solid rgb(235,235,235);
    &:hover {
      background-color: var(--globe-offwhite);
    }
    &:first-of-type {
      border-bottom: 1px solid rgb(215,215,215);
    }
  }
  th, td {
    text-align: left;
    width: 1%;
    white-space: nowrap;
  }
  td:nth-child(2), th:nth-child(2) {
    text-align: center;
  }
  td:nth-child(3), th:nth-child(3) {
    padding-left: 20px;
  }
  @media (max-width: 650px) {
    td:nth-child(4), td:nth-child(5), th:nth-child(4), th:nth-child(5) {
      display: none;
    }
    th, td {
      width: auto;
      white-space: initial;
    }
  }
`;

const StateHeader = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  max-width: 1000px;
  margin: 20px auto 0;
  width: 90%;
`;

const LocationButton = styled.button`
  padding: 0;
  background: none;
  border: none;
  color: var(--globe-link);
`;

const NoLocationGraph = styled.p`
  max-width: 700px;
  font-size: 1rem;
  line-height: 1.375rem;
  margin: 20px auto;
`;

const TownCell = styled.td`
  text-transform: capitalize;
`;

const TableToggle = styled.button`
  border-radius: 3px;
  background-color: var(--globe-red);
  color: white;
  border: none;
  padding: 0.5rem;
  display: block;
  margin: -1rem auto 2rem;
  cursor: pointer;
  transition: all 0.25s ease-out;
  &:hover {
    background-color: var(--offblack);
  }
`;

const SnowTable = ({
  noLocations,
  data,
  selectedState,
  selectedLocation,
  map,
  mapRef,
  stateName,
  fullTableView,
  toggleFullTableView,
}) => {
  let filteredLocations = [];
  if (noLocations === false && data.data !== undefined) {
    // eslint-disable-next-line
    filteredLocations = data.data.features.filter((feature) => feature.properties.state === selectedState);
    filteredLocations.sort((a, b) => {
      if (a.properties.town < b.properties.town) {
        return -1;
      } return 1;
    });
  }

  if (selectedLocation !== 'all') {
    // eslint-disable-next-line
    filteredLocations = filteredLocations.filter((feature) => feature.properties.town == selectedLocation);
  }

  const viewLocationFromTable = useCallback((e) => {
    const long = parseFloat(e.getAttribute('data-longitude'));
    const lat = parseFloat(e.getAttribute('data-latitude'));

    // eslint-disable-next-line
    const feature = data.data.features.find((feature) => feature.properties.lat === lat && feature.properties.long === long);

    mapInteractions.flyToLocationFromTable(
      map.current,
      [long, lat],
      feature,
    );

    mapRef.current.scrollIntoView();
  }, [data]);

  return (
    <div>
      { // eslint-disable-next-line
        (data.data !== undefined && data.data.features.length === 0) ? (
          <NoLocationGraph className="benton-n4 content">There have been no reports from the National Weather service of snow accumulation across New England in the last 48 hours.</NoLocationGraph>
        ) : filteredLocations.length === 0 ? (
          <NoLocationGraph className="benton-n4 content">There have been no reports of snow accumulation in {stateName} in the last 48 hours. Note, this doesn’t necessarily mean there hasn’t been snow in {stateName}, just that the National Weather Service has not published a report of snow in {stateName}.</NoLocationGraph>
        ) : (
          <React.Fragment>
            <StateHeader className="benton-n6">{stateName}</StateHeader>
            <LocationTable>
              <tbody>
                <tr className="benton-n6">
                  <th>Location</th>
                  <th>Inches</th>
                  <th>Report date/time</th>
                  <th>Reported by</th>
                  <th>Type</th>
                  <th> </th>
                </tr>
                {filteredLocations.map((location, index) => {
                  if (index >= 25 && fullTableView === false) {
                    return null;
                  } return (
                    <tr className="benton-n4" key={`table-row-${location.properties.lat}-${location.properties.long}`}>
                      <TownCell>
                        {formatName(location.properties.town)}, {location.properties.state}
                      </TownCell>
                      <td
                        style={{
                          backgroundColor: findColor(location.properties.snow),
                          color: location.properties.snow > 12 ? 'white' : 'black',
                        }}
                      >
                        {location.properties.snow}
                      </td>
                      <td>{formatDateTime(location)}</td>
                      <td>{location.properties.report_type}</td>
                      <td>{location.properties.snow_source}</td>
                      <td>
                        <LocationButton
                          type="button"
                          data-latitude={location.properties.lat}
                          data-longitude={location.properties.long}
                          data-location={location.properties.town}
                          data-state={location.properties.state}
                          onClick={(e) => viewLocationFromTable(e.currentTarget)}
                        >View map
                        </LocationButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </LocationTable>
            {fullTableView === false
              && filteredLocations.length > 25
              && (
                <TableToggle
                  type="button"
                  className="bentonsanscond-n4"
                  onClick={() => toggleFullTableView(true)}
                >
                  View {(filteredLocations.length - 25).toLocaleString(('en-us'))} more {filteredLocations.length - 25 === 1 ? 'location' : 'locations'}
                </TableToggle>
              )}
          </React.Fragment>
        )
      }
    </div>
  );
};

export default SnowTable;

SnowTable.propTypes = {
  noLocations: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    data: PropTypes.shape({
      features: PropTypes.arrayOf(PropTypes.shape).isRequired,
    }),
  }).isRequired,
  selectedState: PropTypes.string.isRequired,
  selectedLocation: PropTypes.string.isRequired,
  map: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  mapRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollIntoView: PropTypes.func,
    }),
  }).isRequired,
  stateName: PropTypes.string.isRequired,
  fullTableView: PropTypes.bool.isRequired,
  toggleFullTableView: PropTypes.func.isRequired,
};
